import React, { useCallback, useMemo } from 'react';
// @ts-expect-error - TS2614 - Module '"@atlaskit/dynamic-table"' has no exported member 'HeadType'. Did you mean to use 'import HeadType from "@atlaskit/dynamic-table"' instead? | TS2614 - Module '"@atlaskit/dynamic-table"' has no exported member 'RowType'. Did you mean to use 'import RowType from "@atlaskit/dynamic-table"' instead?
import type { HeadType, RowType } from '@atlaskit/dynamic-table';
import ArrowRightIcon from '@atlaskit/icon/glyph/arrow-right';
import { JiraIcon } from '@atlaskit/logo';
import { Box, Inline } from '@atlaskit/primitives';
import { useIntl } from '@atlassian/jira-intl';
import type { ColumnName, MappingState } from '../../../common/types';
import { useContentForCSV } from '../../../common/utils/get-content-for-csv';
import { useImportType } from '../../../controllers/import-state';
import { FieldSelector } from './field-selector';
import { ALL, MAPPED, type Filter } from './filters/constants';
import { IncludeInImport } from './include-in-import';
import messages from './messages';

type TableRowArgs = {
	mappingState: MappingState | null;
	searchTerm: string;
	filter?: Filter;
	isErrorToggleEnabled: boolean;
};

export const useTableHead = () => {
	const { formatMessage } = useIntl();
	const [importType] = useImportType();
	const { fieldMappingTableHeading } = useContentForCSV(importType);

	const tableHead = useMemo<HeadType>(
		() => ({
			cells: [
				{
					key: 'csvField',
					content: fieldMappingTableHeading,
				},
				{
					key: 'arrow',
					width: 8,
				},
				{
					key: 'jiraField',
					content: (
						<Inline space="space.050" alignBlock="center">
							<JiraIcon
								label={formatMessage(messages.jiraFields)}
								size="small"
								appearance="brand"
							/>
							{formatMessage(messages.jiraFields)}
						</Inline>
					),
					width: 30,
				},
				{
					key: 'field-checkbox',
					content: (
						<Inline alignInline="center">
							<Box as="p">{formatMessage(messages.includeInImport)}</Box>
						</Inline>
					),
					width: 15,
				},
			],
		}),
		[fieldMappingTableHeading, formatMessage],
	);

	return tableHead;
};

export const useTableRows = ({
	mappingState,
	searchTerm,
	filter,
	isErrorToggleEnabled,
}: TableRowArgs) => {
	const filterCompare = useCallback(
		(columnName: ColumnName) => {
			if (filter === ALL) {
				return true;
			}
			if (filter === MAPPED) {
				return mappingState?.columns[columnName].field !== null;
			}
			return mappingState?.columns[columnName].field === null;
		},
		[filter, mappingState?.columns],
	);

	const combinedFilter = useCallback(
		(name: ColumnName) =>
			name.toLocaleLowerCase().includes(searchTerm.toLocaleLowerCase()) && filterCompare(name),
		[filterCompare, searchTerm],
	);

	const tableRows = useMemo<RowType[] | undefined>(() => {
		if (mappingState === null) {
			return [];
		}
		const columns =
			(isErrorToggleEnabled ? mappingState.errorsFilter : mappingState.columnOrder) || [];
		return columns.filter(combinedFilter).map((columnName: ColumnName) => ({
			key: `row-${columnName}`,
			cells: [
				{
					key: `${columnName}-name`,
					content: <Box as="p">{columnName}</Box>,
				},
				{
					key: `${columnName}-arrow`,
					content: <ArrowRightIcon label="" size="medium" />,
				},
				{
					key: `${columnName}-jira-field`,
					content: <FieldSelector fieldMap={mappingState.columns[columnName]} />,
				},
				{
					key: `${columnName}-checkbox`,
					content: (
						<Inline alignInline="center">
							<IncludeInImport fieldMap={mappingState.columns[columnName]} />
						</Inline>
					),
				},
			],
		}));
	}, [combinedFilter, isErrorToggleEnabled, mappingState]);

	return tableRows;
};
